import React, { useEffect, useState } from 'react';
import { Button, Text, TextInput, View } from "../../Components/Themed"
import { FlatList, Image, Platform } from 'react-native';
import { Icons, Switch, Toggle } from '../../Components';
import { BracketApi, BracketComeptitionApi, BracketCompetitionHelpers } from '../api';
import type { BracketCompetitionProps, BracketCompetitionScoringRuleProps, CompanyProps, BracketProps, BracketRoundProps, CompetitionPlayerBracketProps, CompetitionResultTypeProps, LeagueProps, GroupProps } from '../../types';
import { useColors } from '../../constants/useColors';
import moment from 'moment-mini';
import SearchBox from '../../Components/SearchBox';
import ImageUploader from '../../Components/ImageUploader';
import { CompanySelector, GroupSelector } from '../../CreateEngagement';
import { CreateEngagementApi } from '../../CreateEngagement/api';

const sections = ['header', 'share', 'toggle', 'select_bracket', 'info', 'scoring', 'settings'];

type ManageBracketCompetitionFormProps = {
    bracket_competition_id:string,
    player_id:string,
    onShareCompetition?:(bracket_competition:BracketCompetitionProps) => void,
    onFocusPosition?:(pos:{ x:number, y:number, height:number, width:number }) => void
    onFail: (message?:string) => void,
    onClose: () => void
}

const ManageBracketCompetitionForm = ({ player_id, bracket_competition_id, onFail, onFocusPosition, onClose, onShareCompetition }:ManageBracketCompetitionFormProps) => {
    const Colors = useColors();
    const [ action_loading, setActionLoading ] = useState(false);
    const [ active_toggle, setActiveToggle ] = useState('info');
    const [ scoring_loading, setScoringLoading ] = useState(false);
    const [ info_loading, setInfoLoading ] = useState(false);
    const [ settings_loading, setSettingsLoading ] = useState(false);

    const [ bracket_search, setBracketSearch ] = useState<{
        visible: boolean,
        value: string
    }>({
        visible: false,
        value: ''
    });
    const [ scoring_data, setScoringData ] = useState<{
        bracket_scoring_rules:BracketCompetitionScoringRuleProps[],
        draft_scoring_rules:BracketCompetitionScoringRuleProps[],
    }>({
        bracket_scoring_rules: [],
        draft_scoring_rules: []
    });
    const { draft_scoring_rules, bracket_scoring_rules } = scoring_data;
    const [ form_data, setFormData ] = useState<{
        bracket_competition?:BracketCompetitionProps,
        selected_bracket?:BracketProps,
        competition_result_types:CompetitionResultTypeProps[],
        competition_player_brackets:CompetitionPlayerBracketProps[],
        active_league?:LeagueProps,
        selected_bracket_rounds:BracketRoundProps[],
        leagues:LeagueProps[],
        companies:CompanyProps[],
        groups:GroupProps[],
        available_brackets:BracketProps[],
        draft_competition?:BracketCompetitionProps,
    }>({
        leagues: [],
        available_brackets: [],
        competition_player_brackets:[],
        competition_result_types:[],
        companies:[],
        groups:[],
        selected_bracket_rounds:[]
    });
    const { competition_result_types, companies, groups, bracket_competition, available_brackets, competition_player_brackets, active_league, selected_bracket_rounds, leagues, selected_bracket, draft_competition} = form_data;
    const unique_league_ids = [ ...new Set(available_brackets.map(b => b.league_id)) ]

    let filtered_brackets = [...available_brackets]
    if(active_league){
        filtered_brackets = filtered_brackets.filter(b => b.league_id == active_league.league_id)
    }
    if(bracket_search.visible){
        filtered_brackets = filtered_brackets.filter(b => `${b.bracket_name.toLowerCase()}`.includes(bracket_search.value.toLowerCase()));
    }
    const { info, settings, bracket, scoring } = BracketCompetitionHelpers.isBracketCompetitionEditable(competition_player_brackets, bracket_competition)
    const { info_valid, settings_valid, bracket_valid, scoring_valid } = BracketCompetitionHelpers.isBracketValid(bracket_scoring_rules, selected_bracket_rounds, competition_result_types, bracket_competition )
    const valid = info_valid && settings_valid && bracket_valid && scoring_valid ? true : false
    const can_delete = moment().isBefore(moment(bracket_competition?.scheduled_datetime)) && competition_player_brackets.length == 0 ? true : false
    const active_company = companies.find(c => c.company_id == bracket_competition?.company_id);
    const active_group = groups.find(g => g.group_id == bracket_competition?.group_id);

    useEffect(() => {
        BracketApi.setEnvironment();
        CreateEngagementApi.setEnvironment();
        if(!bracket_competition_id){ return }
        getData(bracket_competition_id);
    },[bracket_competition_id])

    const getData = async(id:string) => {
        let bk:BracketProps | undefined = undefined
        let bk_rounds:BracketRoundProps[] = []
        let bk_options = await BracketApi.getActiveBrackets();
        const options = await BracketComeptitionApi.getCompetitionOptions();
        const bk_resp = await BracketComeptitionApi.getBracketCompetitionById(id);
        if(!bk_resp){ return onFail() }
        if(bk_resp.bracket_competition.admin_id != player_id){ return onFail('Not Eligible to edit this competition') }
        if(bk_resp.bracket_competition.status == 'inactive'){ return onFail('This competition has been deleted and cannot be edited') }
        const lgs = await BracketApi.getLeagues();
        if(bk_resp.bracket_competition.bracket_id){
            let bracket_resp = await BracketApi.getBracketById(bk_resp.bracket_competition.bracket_id);
            if(!bracket_resp){ return onFail() }
            bk = bracket_resp.bracket
            bk_rounds = bracket_resp.bracket_rounds
        }

        const s_companies = await CreateEngagementApi.getMyCompanies();
        const s_groups = await CreateEngagementApi.getMyGroups(0);
        //Lets Check if we need to adjust scoring rules right away!
        let draft_rules = BracketCompetitionHelpers.checkRules(id, bk_rounds, bk_resp.bracket_competition_scoring_rules);
        if(JSON.stringify(draft_rules) != JSON.stringify(bk_resp.bracket_competition_scoring_rules)){
            //Need to save them and update them right away!
            draft_rules = await BracketComeptitionApi.updateBracketCompetitionScoringRules(id, draft_rules);
        }
        setScoringData({
            bracket_scoring_rules: draft_rules,
            draft_scoring_rules: draft_rules
        })
        setFormData({
            ...form_data,
            bracket_competition: bk_resp.bracket_competition,
            draft_competition: bk_resp.bracket_competition,
            competition_result_types: options.competition_result_types.filter(crt => crt.type != 'outpace'),
            leagues: lgs,
            groups: s_groups.groups,
            companies: s_companies.companies,
            competition_player_brackets: bk_resp.competition_player_brackets,
            available_brackets: bk_options.filter(bk => moment(bk.scheduled_datetime).isAfter(moment())),
            selected_bracket_rounds: bk_rounds,
            selected_bracket: bk,
        });
    }

    const handleSelectBracket = async(bracket_id:string) => {
        if(!draft_competition){ return }
        if(!bracket){ return } //Bracket is not editable!
        const bk_resp = await BracketApi.getBracketById(bracket_id);
        if(!bk_resp){ return }
        let new_bc = await BracketComeptitionApi.updateBracketCompetition({ ...draft_competition, bracket_id, scheduled_datetime: bk_resp.bracket.scheduled_datetime })

        setFormData({
            ...form_data,
            draft_competition: new_bc,
            bracket_competition: new_bc,
            selected_bracket: bk_resp.bracket,
            selected_bracket_rounds: bk_resp.bracket_rounds
        });

        let new_rules = BracketCompetitionHelpers.checkRules(bracket_competition_id, bk_resp.bracket_rounds, bracket_scoring_rules);
        new_rules = await BracketComeptitionApi.updateBracketCompetitionScoringRules(bracket_competition_id, new_rules);
        setScoringData({
            draft_scoring_rules: new_rules,
            bracket_scoring_rules: new_rules
        });

        //Now we need to auto update the scheduled time with the bracket start time
    }

    const renderLeagues = (data:{item:string, index:number}) => {
        const league = leagues.find(l => l.league_id == data.item);
        if(!league){ return <></> }
        const selected = active_league?.league_id == data.item ? true : false
        return (
            <Button float style={{ margin:2, flexDirection:'row', alignItems:'center', padding:10, borderRadius:100, backgroundColor:selected ? Colors.text.highlight : undefined }} onPress={() => selected ? setFormData({ ...form_data, active_league: undefined }): setFormData({ ...form_data, active_league:league })}>
                <Image
                    style={{ height:25, width:25 }}
                    source={{ uri: league.league_image }}
                    resizeMode='cover'
                />
                <View transparent style={{ flex:1, marginLeft:5 }}>
                    <Text theme='h2'>{league.league_name}</Text>
                </View>
            </Button>
        )
    }

    const handleUpdateScoringRule = (scoring_rule:BracketCompetitionScoringRuleProps) => {
        if(isNaN(scoring_rule.win_points)){ return }
        setScoringData({
            ...scoring_data,
            draft_scoring_rules:draft_scoring_rules.filter(sr => sr.bracket_competition_scoring_rule_id != scoring_rule.bracket_competition_scoring_rule_id).concat(scoring_rule)
        })
    }
    
    const renderBracketRounds = (data: { item:BracketRoundProps, index:number }) => {
        const scoring_rule = draft_scoring_rules.find(bsr => bsr.round_number == data.item.round_number)
        if(!scoring_rule){ return <></> }
        return (
            <View transparent style={{ borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <View transparent style={{ flexDirection:'row', alignItems:'center', padding:5 }}>
                    <View transparent style={{ flex:1 }}>
                        <Text theme='h2'>{data.item.round_name}</Text>
                        <View transparent style={{ flexDirection:'row', alignItems:'center', padding:3 }}>
                            <Switch
                                size={12}
                                disabled={!scoring}
                                value={scoring_rule.underdog_bonus}
                                switch_type='on_off'
                                onChange={(val) => {
                                    setScoringData({
                                        ...scoring_data,
                                        draft_scoring_rules:draft_scoring_rules.filter(sr => sr.bracket_competition_scoring_rule_id != scoring_rule.bracket_competition_scoring_rule_id).concat({ ...scoring_rule, underdog_bonus: val, underdog_multiplier: val ? 1.5: 0 })
                                    })
                                }}
                            />
                            <Text style={{ marginLeft:5 }} theme='description'>Underdog Bonus</Text>
                        </View>
                    </View>
                    <TextInput
                        value={scoring_rule.win_points.toString() as unknown as string}
                        editable={scoring}
                        style={{ textAlign:'center', width:80 }}
                        onChangeText={(text) => handleUpdateScoringRule({ ...scoring_rule, win_points: text as unknown as number })}
                    />
                    {scoring_rule.underdog_bonus ?
                    <View transparent style={{ flexDirection:'row', alignItems:'center' }}>
                        <Text style={{ padding:4 }} theme='h1'>X</Text>
                        <TextInput
                            value={scoring_rule.underdog_multiplier.toString() as unknown as string}
                            style={{ textAlign:'center', width:65 }}
                            editable={scoring}
                            onChangeText={(text) => handleUpdateScoringRule({ ...scoring_rule, underdog_multiplier: text  as unknown as number })}
                        />
                    </View>
                    :<></>}
                </View>
                {scoring_rule.underdog_bonus ?
                <View transparent style={{ padding:5 }}>
                    <Text theme='warning'>If a player selects the lower seed to win correctly, they will be awarded {scoring_rule.win_points * scoring_rule.underdog_multiplier} points</Text>
                </View>
                :<></>}
            </View>
        )
    }

    const renderResults = (data:{ item:CompetitionResultTypeProps, index:number }) => {
        const selected = draft_competition?.competition_result_type_id == data.item.competition_result_type_id ? true : false;
        return (
            <Button 
                disabled={!settings}
            style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}
                onPress={() => {
                    if(!draft_competition){ return }
                    setFormData({
                        ...form_data,
                        draft_competition: { ...draft_competition, competition_result_type_id:data.item.competition_result_type_id }
                    })
                }}
            >
                <View style={{ height:25, width: 25, borderWidth:1, borderRadius:100, justifyContent:'center', alignItems:'center', borderColor:Colors.text.action }}>
                    {selected ?
                    <View style={{ height:15, width:15, borderRadius:100, backgroundColor:Colors.text.action }} />
                    :<></>}
                </View>
                <View style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h2'>{data.item.label}</Text>
                    <Text style={{ marginTop:3 }} theme='description'>{data.item.description}</Text>
                </View>
            </Button>
        )
    }

    const renderBrackets = (data:{ item:BracketProps, index:number }) => {
        const league = leagues.find(l => l.league_id == data.item.league_id);
        const selected = selected_bracket?.bracket_id == data.item.bracket_id ? true : false
        return (
            <Button 
                disabled={!bracket}
            style={{ borderRadius:0, padding:10, borderBottomWidth:1, borderBottomColor:Colors.borders.light, flexDirection:'row', alignItems:'center' }}
            onPress={() => handleSelectBracket(data.item.bracket_id)}>
                <View style={{ marginRight:10, height:20, width:20, borderRadius:2, borderWidth:1, borderColor:Colors.text.action, justifyContent:'center', alignItems:'center' }}>
                    {selected ?
                    <View style={{ height:15, width:15, backgroundColor:Colors.text.action }} />
                    :<></>}
                </View>
                {league ?
                <Image
                    style={{ height:25, width:25 }}
                    source={{ uri: league.league_image }}
                    resizeMode='cover'
                />
                :
                <Icons.BracketIcon size={18} color={Colors.text.h1} />
                }
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h2'>{data.item.bracket_name}</Text>
                    <Text style={{ marginTop:3 }} theme='description'>{moment(data.item.scheduled_datetime).format('YYYY-MM-DD hh:mm a')}</Text>
                </View>
            </Button>
        )
    }


    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                if(!bracket_competition || !draft_competition){ return <></> }
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                        <ImageUploader
                                public_id={`${draft_competition.bracket_competition_id}_image_${Math.random()}`}
                                onFinishUpload={async(obj) => {
                                    const new_bc = await BracketComeptitionApi.updateBracketCompetition({ ...draft_competition, image: { ...obj, url: obj.secure_url } })
                                    setFormData({
                                        ...form_data,
                                        draft_competition: new_bc,
                                        bracket_competition: new_bc
                                    })
                                }}
                        >
                            {bracket_competition.image?.url ?
                            <View transparent style={{ justifyContent:'center', alignItems:'center' }}>
                                <Image
                                    source={{ uri: bracket_competition.image.url }}
                                    style={{ height:30, width:30 }}
                                    resizeMode='cover'
                                />
                            </View>
                            :
                            <Text theme='action'>Add Image</Text>
                            }
                        </ImageUploader>
                        <View transparent style={{ flex:1 }}>
                            <Text theme='h1'>{bracket_competition.competition_name}</Text>
                            <Text theme='description' style={{ marginTop:4 }}>{bracket_competition.competition_description}</Text>
                        </View>
                        <View style={{ flexDirection:'row' }}>
                            {bracket_competition.status == 'pending' ?
                            <Button
                                disabled={!valid || action_loading}
                                loading={action_loading}
                                style={{ opacity: action_loading || !valid ? 0.5 : 1 }}
                                type='success'
                                title='ACTIVATE'
                                onPress={async() => {
                                    if(action_loading){ return }
                                    setActionLoading(true);
                                    let new_bc = await BracketComeptitionApi.updateBracketCompetition({ ...draft_competition, status : 'scheduled' });
                                    setFormData({
                                        ...form_data,
                                        draft_competition: new_bc,
                                        bracket_competition: new_bc
                                    });
                                    setActionLoading(false)
                                }}
                            />
                            :<></>}
                            {can_delete ?
                            <Button
                                disabled={action_loading}
                                loading={action_loading}
                                style={{ opacity: action_loading ? 0.5 : 1 }}
                                type='error'
                                title='DELETE'
                                onPress={async() => {
                                    if(action_loading){ return }
                                    if(Platform.OS == 'web'){
                                        let confirmed = await confirm('Are you sure?')
                                        if(!confirmed){ return  }
                                    }
                                    setActionLoading(true);
                                    let new_bc = await BracketComeptitionApi.updateBracketCompetition({ ...draft_competition, status : 'inactive' });
                                    setFormData({
                                        ...form_data,
                                        draft_competition: new_bc,
                                        bracket_competition: new_bc
                                    });
                                    setActionLoading(false);
                                    onClose()
                                }}
                            />
                            :<></>}
                        </View>
                    </View>
                )
            case 'share':
                if(!onShareCompetition || !bracket_competition){ return <></> }
                return (
                    <Button style={{ marginTop:1, marginBottom:10, flexDirection:'row', alignItems:'center', padding:10, borderRadius:0, backgroundColor:Colors.buttons.background.action }}
                    onPress={() => onShareCompetition(bracket_competition)}>
                        <Icons.BracketIcon size={18} color={Colors.text.white} />
                        <View transparent style={{ flex:1, marginLeft:10, marginRight:10 }}>
                            <Text theme='h1' color={Colors.text.white}>INVITE OTHERS</Text>
                            <Text theme='description'>Share this competition with others to get started and build the pot!</Text>
                        </View>
                        <View float style={{ padding:5 }}>
                            <Icons.ShareIcon size={12} color={Colors.text.action} />
                        </View>
                    </Button>
                )
            case 'toggle':
                return (
                    <View style={{ padding:10, marginBottom:10 }}>
                        <Toggle
                            toggle_padding={10}
                            options={[
                                { key: 'bracket', valid_string:'bracket_valid', label:'Bracket', icon: <Icons.BracketIcon size={12} color={bracket_valid ? Colors.text.success : Colors.text.warning} /> },
                                { key: 'info', valid_string: 'info_valid', label: 'Info', icon: <Icons.MenuIcon size={12} color={info_valid ? Colors.text.success : Colors.text.warning} /> },
                                { key: 'scoring', valid_string: 'scoring_valid', label: 'Scoring', icon: <Icons.PodiumIcon size={12} color={scoring_valid ? Colors.text.success : Colors.text.warning} /> },
                                { key: 'settings', valid_string: 'settings_valid', label: 'Settings', icon: <Icons.SettingsIcon size={14} color={settings_valid ? Colors.text.success : Colors.text.warning} /> },

                            ]}
                            title_renderer={(option) => {
                                const selected = option.key == active_toggle ? true : false
                                return (
                                    <View transparent style={{ alignItems:'center', justifyContent:'center' }}>
                                        {option.icon}
                                        <Text style={{ marginLeft:5 }} theme='h1' color={selected ? Colors.text.action : Colors.text.h1}>{option.label}</Text>
                                    </View>
                                )
                            }}
                            selected_option={active_toggle}
                            onSelectOption={(key) => setActiveToggle(key)}
                        />
                    </View>
                )
            case 'select_bracket':
                if(active_toggle != 'bracket'){ return <></> }
                const selected_league = leagues.find(l => l.league_id == selected_bracket?.league_id);
                return (
                    <View style={{ flex:1 }}>
                        {selected_bracket ?
                        <View float style={{ margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                <View transparent style={{ flex:1 }}>
                                    <Text theme='h2'>SELECTED BRACKET</Text>
                                    <Text style={{ marginTop:3 }} theme='description'>Select a league to filter bracket options</Text>
                                </View>
                            </View>
                            <Button style={{ borderRadius:0, padding:10, borderBottomWidth:1, borderBottomColor:Colors.borders.light, flexDirection:'row', alignItems:'center' }}>
                                {selected_league ?
                                <Image
                                    style={{ height:25, width:25 }}
                                    source={{ uri: selected_league.league_image }}
                                    resizeMode='cover'
                                />
                                :
                                <Icons.BracketIcon size={18} color={Colors.text.h1} />
                                }
                                <View transparent style={{ flex:1, marginLeft:10 }}>
                                    <Text theme='h2'>{selected_bracket.bracket_name}</Text>
                                    <Text style={{ marginTop:3 }} theme='description'>{moment(selected_bracket.scheduled_datetime).format('YYYY-MM-DD hh:mm a')}</Text>
                                </View>
                            </Button>
                        </View>
                       :<></>}
                       <View style={{ padding:20, backgroundColor:Colors.text.action }}>
                            <Text theme='h1' textAlign='center'>{bracket ? 'CHANGE BRACKET!':'BRACKET IS LOCKED'}</Text>
                            {bracket ?
                            <Text theme='description' textAlign='center'>Use the options below to select new bracket</Text>
                            :<></>}
                       </View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h2'>LEAGUE OPTIONS</Text>
                                <Text style={{ marginTop:3 }} theme='description'>Select a league to filter bracket options</Text>
                            </View>
                        </View>
                        <View style={{ padding:5 }}>
                            <FlatList
                                data={unique_league_ids}
                                key='league_selector'
                                keyExtractor={(item) => item}
                                renderItem={renderLeagues}
                                horizontal
                            />
                        </View>
                        <View float style={{ margin:5 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                <View transparent style={{ flex:1 }}>
                                    <Text theme='h2'>BRACKET OPTIONS</Text>
                                    <Text style={{ marginTop:3 }} theme='description'>Select a Bracket below or search</Text>
                                </View>
                                <Button float onPress={() => setBracketSearch({ ...bracket_search, visible: !bracket_search.visible, value: '' })}>
                                    {bracket_search.visible ?
                                    <Icons.CloseIcon size={14} color={Colors.text.error} />
                                    :
                                    <Icons.SearchIcon size={14} color={Colors.text.h1} />
                                    }
                                </Button>
                            </View>
                            {bracket_search.visible ?
                            <View style={{ padding:5 }}>
                                <SearchBox
                                    onFocusPosition={onFocusPosition}
                                    onChange={(text) => setBracketSearch({ ...bracket_search, value:text })}
                                    hide_search_button
                                />
                            </View>
                            :<></>}
                            <FlatList
                                data={filtered_brackets}
                                keyExtractor={(item) => item.bracket_id.toString()}
                                key={'brackets_list'}
                                renderItem={renderBrackets}

                            />
                        </View>
                    </View>
                )
            case 'info':
                if(active_toggle != 'info'){ return <></> }
                if(!draft_competition){ return <></> }
                const comp_changed = JSON.stringify(draft_competition) != JSON.stringify(bracket_competition) ? true : false
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Update Competition Information</Text>
                            </View>
                            <Button
                                type='success'
                                title='SAVE'
                                style={{ flex:1, opacity:comp_changed && !info_loading ? 1: 0.5 }}
                                disabled={!comp_changed || info_loading}
                                loading={info_loading}
                                onPress={async() => {
                                    if(info_loading){ return }
                                    setInfoLoading(true);
                                    const new_bc = await BracketComeptitionApi.updateBracketCompetition(draft_competition);
                                    setFormData({
                                        ...form_data,
                                        bracket_competition: new_bc,
                                        draft_competition: new_bc
                                    })
                                    setInfoLoading(false);
                                }}
                            />
                        </View>
                        <View type='body'>
                            <View style={{ padding:10 }}>
                                <Text theme='h2' style={{ marginBottom:8 }}>Competition Name</Text>
                                <TextInput
                                    editable={info}
                                    onFocusPosition={onFocusPosition}
                                    value={draft_competition.competition_name}
                                    placeholder='Enter a memorable name ...'
                                    onChangeText={(text) => setFormData({
                                        ...form_data,
                                        draft_competition: { ...draft_competition, competition_name: text }
                                    })}
                                />
                            </View>
                            <View style={{ padding:10 }}>
                                <Text theme='h2' style={{ marginBottom:8 }}>Description</Text>
                                <TextInput
                                    editable={info}
                                    onFocusPosition={onFocusPosition}
                                    value={draft_competition.competition_description}
                                    placeholder='Enter a memorable description ...'
                                    onChangeText={(text) => setFormData({
                                        ...form_data,
                                        draft_competition: { ...draft_competition, competition_description: text }
                                    })}
                                />
                            </View>
                            {groups.length > 0 ?
                            <View style={{ padding:10 }}>
                                <Text theme='h2' style={{ marginBottom:8 }}>Group</Text>
                                <GroupSelector
                                    groups={groups}
                                    active_group={active_group}
                                    onSelectGroup={async(g) => {
                                        if(info_loading){ return }
                                        setInfoLoading(true);
                                        const new_bc = await BracketComeptitionApi.updateBracketCompetition({ ...draft_competition, group_id: g?.group_id });
                                        setFormData({
                                            ...form_data,
                                            bracket_competition: new_bc,
                                            draft_competition: new_bc
                                        })
                                        setInfoLoading(false);
                                    }}
                                />
                            </View>
                            :<></>}
                            {companies.length > 0 ?
                            <View style={{ padding:10 }}>
                                <Text theme='h2' style={{ marginBottom:8 }}>Company</Text>
                                <CompanySelector
                                    companies={companies}
                                    active_company={active_company}
                                    onSelectCompany={async(c) => {
                                        if(info_loading){ return }
                                        setInfoLoading(true);
                                        const new_bc = await BracketComeptitionApi.updateBracketCompetition({ ...draft_competition, company_id: c?.company_id });
                                        setFormData({
                                            ...form_data,
                                            bracket_competition: new_bc,
                                            draft_competition: new_bc
                                        })
                                        setInfoLoading(false);
                                    }}
                                />
                            </View>
                            :<></>}
                            <View type='row' style={{ padding:10 }}>
                                <View transparent style={{ flex:1 }}>
                                    <Text theme='h2'>Start Time</Text>
                                    <Text style={{ marginTop:3 }} theme='warning'>Defaults to selected bracket</Text>
                                </View>
                                <Text theme='h1' color={Colors.text.descriptionLight} size={14}>{moment(draft_competition.scheduled_datetime).format('YYYY-MM-DD @ hh:mm a')}</Text>
                            </View>
                            <View type='row' style={{ padding:10 }}>
                                <View transparent style={{ flex:1 }}>
                                    <Text theme='h2'>Private</Text>
                                    <Text style={{ marginTop:3 }} theme='warning'>This competition is private - use share link or provide code to invite entries</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                )
            case 'scoring':
                if(active_toggle != 'scoring'){ return <></> }
                const is_changed = JSON.stringify(draft_scoring_rules) != JSON.stringify(bracket_scoring_rules) ? true : false
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Update Competition Scoring Rules</Text>
                            </View>
                            <Button
                                type='success'
                                title='SAVE RULES'
                                style={{ flex:1, opacity:is_changed && !scoring_loading ? 1: 0.5 }}
                                disabled={!is_changed || scoring_loading}
                                loading={scoring_loading}
                                onPress={async() => {
                                    if(scoring_loading){return}
                                    setScoringLoading(true);
                                    let new_rules = await BracketComeptitionApi.updateBracketCompetitionScoringRules(bracket_competition_id, draft_scoring_rules);
                                    setScoringLoading(false);
                                    setScoringData({
                                        bracket_scoring_rules: new_rules,
                                        draft_scoring_rules: new_rules
                                    });
                                }}
                            />
                        </View>
                        <View float type='body' style={{ margin:10 }}>
                            <FlatList
                                data={selected_bracket_rounds.sort((a,b) => a.round_number - b.round_number)}
                                renderItem={renderBracketRounds}
                                key='scoring_rules'
                                extraData={[JSON.stringify(draft_scoring_rules)]}
                                keyExtractor={(item) => item.bracket_round_id.toString()}
                            />
                        </View>
                    </View>
                )
            case 'settings':
                if(!draft_competition){ return <></> }
                if(active_toggle != 'settings'){ return <></> }
                const settings_changed = JSON.stringify(draft_competition) != JSON.stringify(bracket_competition) ? true : false
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Update Competition Information</Text>
                            </View>
                            <Button
                                type='success'
                                title='SAVE'
                                style={{ flex:1, opacity:settings_changed && !settings_loading ? 1: 0.5 }}
                                disabled={!settings || !settings_changed || settings_loading}
                                loading={settings_loading}
                                onPress={async() => {
                                    if(settings_loading){ return }
                                    setSettingsLoading(true);
                                    const new_bc = await BracketComeptitionApi.updateBracketCompetition(draft_competition);
                                    setFormData({
                                        ...form_data,
                                        bracket_competition: new_bc,
                                        draft_competition: new_bc
                                    })
                                    setSettingsLoading(false);
                                }}
                            />
                        </View>
                        <View type='body'>
                            <View type='row' style={{ padding:10 }}>
                                <View style={{ flex:1 }}>
                                    <Text theme='h2' style={{ marginBottom:8 }}>Ticket Price</Text>
                                    <Text theme='description' style={{ marginTop:4 }}>How much does it cost to enter the competitions?</Text>
                                </View>
                                <TextInput
                                    onFocusPosition={onFocusPosition}
                                    editable={settings}
                                    style={{ textAlign:'center', width:100 }}
                                    value={draft_competition.buy_in.toString()  as unknown as string}
                                    placeholder='$5'
                                    onChangeText={(text) => setFormData({
                                        ...form_data,
                                        draft_competition: { ...draft_competition, buy_in: text  as unknown as number }
                                    })}
                                />
                            </View>
                            <View type='row' style={{ padding:10 }}>
                                <View style={{ flex:1 }}>
                                    <Text theme='h2' style={{ marginBottom:8 }}>Brackets / Person</Text>
                                    <Text theme='description' style={{ marginTop:4 }}>How many brackets can a single player enter?</Text>
                                </View>
                                <TextInput
                                    editable={settings}
                                    onFocusPosition={onFocusPosition}
                                    style={{ textAlign:'center', width:100 }}
                                    value={draft_competition.max_brackets_per_player.toString() as unknown as string}
                                    placeholder='1'
                                    onChangeText={(text) => setFormData({
                                        ...form_data,
                                        draft_competition: { ...draft_competition, max_brackets_per_player: text as unknown as number }
                                    })}
                                />
                            </View>
                            <View type='row' style={{ padding:10 }}>
                                <View style={{ flex:1 }}>
                                    <Text theme='h2' style={{ marginBottom:8 }}>Available Tickets</Text>
                                    <Text theme='description' style={{ marginTop:4 }}>How many total brackets can enter?</Text>
                                </View>
                                <TextInput
                                    onFocusPosition={onFocusPosition}
                                    editable={settings}
                                    style={{ textAlign:'center', width:100 }}
                                    value={draft_competition.max_brackets.toString() as unknown as string}
                                    placeholder='1'
                                    onChangeText={(text) => setFormData({
                                        ...form_data,
                                        draft_competition: { ...draft_competition, max_brackets: text  as unknown as number }
                                    })}
                                />
                            </View>
                            <View float style={{ margin:10 }}>
                                <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                    <View transparent style={{ flex:1 }}>
                                        <Text theme='h1'>Select Result Type</Text>
                                        <Text style={{ marginTop:3 }} theme='description'>How are winners determined?</Text>
                                    </View>
                                </View>
                                <View transparent>
                                    <FlatList
                                        data={competition_result_types}
                                        key={'results'}
                                        keyExtractor={(item) => item.competition_result_type_id.toString()}
                                        renderItem={renderResults}
                                    />
                                </View>
                            </View>
                        </View>
                    </View>
                )
            default: return <></>
        }
    }
    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1 }}>
                <FlatList
                    data={sections}
                    key='manage_bracket_comp'
                    keyExtractor={(item) => item}
                    renderItem={renderSections}
                />
            </View>
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                <Button
                    style={{ flex:1 }}
                    type='close'
                    title='CLOSE'
                    onPress={() => onClose()}
                />
            </View>
        </View>
    )
}

export default ManageBracketCompetitionForm